import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { sanitizeTenantAttributeSpec, type TenantAttributeSpec } from "@the-real-insight/tri-model";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SpecEditor, { draftOf, emptyDraft, rawOf, type SpecDraft } from "./SpecEditor";

/**
 * Name, type, unit, the values of an enumeration and the fields of a structure — nested as deep as
 * the model allows. Used to add an attribute to the classes of a filter result and to change a set
 * of one attribute as a whole.
 *
 * Checked by `sanitizeTenantAttributeSpec` from the model, the rule the server applies too: what this
 * dialog lets through, the server accepts.
 */

interface Properties {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  initial?: TenantAttributeSpec;
  onDismiss: () => void;
  onConfirm: (spec: TenantAttributeSpec) => Promise<void>;
}

export default function AttributeDialog({ open, title, description, confirmLabel, initial, onDismiss, onConfirm }: Properties) {
  const { t } = useTranslation();

  const [draft, setDraft] = useState<SpecDraft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // A dialog opened again starts from what it is about, not from what was typed the last time.
  useEffect(() => {
    if (!open) return;
    setDraft(initial ? draftOf(initial) : emptyDraft());
    setError(null);
    setSaving(false);
  }, [open, initial]);

  const confirm = async () => {
    const result = sanitizeTenantAttributeSpec(rawOf(draft));
    if ("error" in result) {
      setError(result.error);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onConfirm(result.value);
    } catch (confirmError: any) {
      setError(confirmError?.message ?? t("global.generalError"));
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !saving && onDismiss()}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <SpecEditor draft={draft} depth={1} onChange={setDraft} />

          {error ? (
            <Alert variant="destructive">
              <AlertDescription className="min-w-0 break-words">{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={onDismiss}>
            {t("global.cancel")}
          </Button>
          <Button disabled={saving} onClick={confirm}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
